import DOMPurify from 'dompurify'
import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded'
import ArrowDropUpRounded from '@mui/icons-material/ArrowDropUpRounded'
import { Box, Divider, Typography, alpha } from '@mui/material'

import { optionalClickableProps } from '../../helpers/interactive'
import { PatternMixRow, buildPatternMixTakeaways } from '../../helpers/showcase'
import { focusRingSx } from '../../helpers/styles'

interface PatternMixChartProps {
  /** Pre-computed App-vs-benchmark mix rows (see `getPatternMixRows`). */
  rows: PatternMixRow[]
  /** Viewed game's display name (for the takeaways copy). */
  gameName: string
  /** Benchmark display label — the game's genre, or the comparison target in the compare dialogs. */
  benchmarkLabel: string
  /** Drill into a pattern when its row is clicked. */
  onSelectPattern?: (patternId: string) => void
  /**
   * `'full'` (default) renders the chart with its hint, side takeaways panel
   * and footnote. `'compact'` renders just the diverging chart — keeping the
   * baseline, gridlines, axis and legend so it stays legible — for snippet
   * contexts (e.g. the game Overview) that supply their own heading/hint.
   */
  variant?: 'full' | 'compact'
}

/** A mix row paired with its localised pattern name; `theme` stays the identity. */

const ROW_HEIGHT = 28
const BAR_HEIGHT = 15
const LABEL_COL = 150

/** Candidate tick steps (percentage points), smallest first. */
const TICK_STEPS = [1, 2, 5, 10, 15, 20, 25, 50, 100, 200]

/** Pre-format a gap as a signed percentage string ("+32%", "−28%"). */
const formatGap = (gap: number): string => `${gap > 0 ? '+' : gap < 0 ? '\u2212' : ''}${Math.abs(gap)}%`

/** Tick-axis label ("−20%", "0%", "20%") — no leading "+" on positives, real minus sign on negatives. */
const formatTick = (value: number): string => `${value < 0 ? '\u2212' : ''}${Math.abs(value)}%`

/**
 * Build a symmetric, "nice" tick axis around zero for the diverging chart. Adds
 * ~20% headroom past the largest gap so the longest bar never reaches the edge
 * (leaving room for its value label), then snaps the half-range up to a round
 * step that yields at most ~4 ticks per side.
 */
const buildAxis = (maxAbs: number): { axisMax: number; ticks: number[] } => {
  const headroom = Math.max(1, maxAbs) * 1.2
  const step = TICK_STEPS.find((candidate) => headroom / candidate <= 4) ?? 200
  const axisMax = Math.ceil(headroom / step) * step
  const ticks: number[] = []
  for (let value = -axisMax; value <= axisMax + 1e-9; value += step) ticks.push(Math.round(value))
  return { axisMax, ticks }
}

export const PatternMixChart: FC<PatternMixChartProps> = ({ rows, gameName, benchmarkLabel, onSelectPattern, variant = 'full' }) => {
  const { t } = useTranslation('ua-insights')
  const compact = variant === 'compact'

  const maxAbs = useMemo(() => Math.max(1, ...rows.map((r) => Math.abs(r.gap))), [rows])
  const { axisMax, ticks } = useMemo(() => buildAxis(maxAbs), [maxAbs])
  /** Map a gap value (percentage points) to its horizontal position across the track (0–100%). */
  const posFor = useMemo(() => (value: number) => 50 + (value / axisMax) * 50, [axisMax])

  const takeawaysHtml = useMemo(
    () => DOMPurify.sanitize(buildPatternMixTakeaways(t, gameName, benchmarkLabel, rows), { ALLOWED_TAGS: ['strong'], ALLOWED_ATTR: [] }),
    [t, gameName, benchmarkLabel, rows]
  )

  const overIndexes = useMemo(() => rows.filter((r) => r.gap > 0), [rows])
  const underIndexes = useMemo(() => rows.filter((r) => r.gap < 0).sort((a, b) => a.gap - b.gap), [rows])

  if (!rows.length) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!compact && (
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5 }}>{t('pattern_mix_hint', { benchmark: benchmarkLabel })}</Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 3 }, alignItems: 'stretch' }}>
        {/* Diverging gap chart */}
        <Box sx={{ flex: compact ? '1 1 100%' : '2 1 480px', minWidth: compact ? 0 : 300, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 0.25 }}>
            <ChartGrid ticks={ticks} posFor={posFor} />
            {rows.map((row) => (
              <PatternMixBar key={row.id} row={row} axisMax={axisMax} onSelect={onSelectPattern} />
            ))}
          </Box>
          <AxisLabels ticks={ticks} posFor={posFor} />
          <ChartLegend
            overLabel={t('pattern_mix_legend_over', { benchmark: benchmarkLabel })}
            underLabel={t('pattern_mix_legend_under', { benchmark: benchmarkLabel })}
          />
        </Box>

        {/* Takeaways + over/under-index lists — full chart only. */}
        {!compact && (
          <Box
            sx={{
              flex: '1 1 270px',
              minWidth: 248,
              p: 2,
              borderRadius: 3,
              backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.035),
              border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.06)}`,
            }}
          >
            {takeawaysHtml && (
              <Box sx={{ mb: 2 }}>
                <SummaryHeading gutter>{t('pattern_mix_takeaways_heading')}</SummaryHeading>
                <Typography
                  component="div"
                  sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.55, '& strong': { color: 'text.primary', fontWeight: 700 } }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: takeawaysHtml }}
                />
              </Box>
            )}

            {overIndexes.length > 0 && <IndexList heading={t('pattern_mix_over_heading')} rows={overIndexes} positive />}
            {overIndexes.length > 0 && underIndexes.length > 0 && <Divider sx={{ my: 1.5 }} />}
            {underIndexes.length > 0 && <IndexList heading={t('pattern_mix_under_heading')} rows={underIndexes} positive={false} />}
          </Box>
        )}
      </Box>

      {!compact && (
        <>
          <Divider />
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', lineHeight: 1.5 }}>
            {t('pattern_mix_footer', { benchmark: benchmarkLabel })}
          </Typography>
        </>
      )}
    </Box>
  )
}

/** Small uppercase section label used inside the summary panel. */
const SummaryHeading: FC<{ children: ReactNode; gutter?: boolean }> = ({ children, gutter = false }) => (
  <Typography
    sx={{
      fontSize: '0.625rem',
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      lineHeight: 1.2,
      mb: gutter ? 0.75 : 0,
    }}
  >
    {children}
  </Typography>
)

interface PatternMixBarProps {
  row: PatternMixRow
  axisMax: number
  onSelect?: (patternId: string) => void
}

/** A single diverging row: pattern label + a bar growing left (under) or right (over) of a centre line. */
const PatternMixBar: FC<PatternMixBarProps> = ({ row, axisMax, onSelect }) => {
  const positive = row.gap >= 0
  const barColor = positive ? 'success.main' : 'error.main'
  const fillPct = (Math.abs(row.gap) / axisMax) * 50
  const clickable = !!onSelect

  return (
    <Box
      {...optionalClickableProps(clickable ? () => onSelect?.(row.id) : undefined)}
      sx={{
        display: 'grid',
        gridTemplateColumns: `${LABEL_COL}px 1fr`,
        alignItems: 'center',
        gap: 1.25,
        flex: '1 1 0',
        minHeight: ROW_HEIGHT,
        maxHeight: 48,
        px: 0.5,
        borderRadius: 1.5,
        position: 'relative',
        zIndex: 1,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background-color 0.12s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
        // Mirrors the Motivation Blind Spots card hover: the row tints, its bar
        // "comes alive" (swells vertically + brightens) and its value pops, all
        // on the same spring easing so the Overview reads as one language.
        '& .pmx-bar': { transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.25s ease, box-shadow 0.25s ease' },
        '& .pmx-value': { transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)' },
        '&:hover': clickable
          ? {
              backgroundColor: 'action.hover',
              '& .pmx-label': { color: 'text.primary' },
              '& .pmx-bar': { transform: 'translateY(-50%) scaleY(1.32)', filter: 'saturate(1.15) brightness(1.05)' },
              '& .pmx-value': { transform: 'translateY(-50%) scale(1.14)' },
            }
          : undefined,
        ...focusRingSx(1),
      }}
    >
      <Typography
        className="pmx-label"
        sx={{
          fontSize: '0.8125rem',
          color: 'text.secondary',
          textAlign: 'right',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'color 0.12s ease',
        }}
      >
        {row.label}
      </Typography>

      <Box sx={{ position: 'relative', height: '100%' }}>
        <Box
          className="pmx-bar"
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            // Swell the bar in place from the centre baseline on hover.
            transformOrigin: positive ? 'left center' : 'right center',
            height: BAR_HEIGHT,
            minWidth: 2,
            width: `${fillPct}%`,
            backgroundColor: barColor,
            boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.common.black, 0.12)}`,
            ...(positive ? { left: '50%', borderRadius: '3px 5px 5px 3px' } : { right: '50%', borderRadius: '5px 3px 3px 5px' }),
          }}
        />
        <Typography
          className="pmx-value"
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            // Pop from the bar's leading edge so the value grows toward the label.
            transformOrigin: positive ? 'left center' : 'right center',
            fontSize: '0.6875rem',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: barColor,
            whiteSpace: 'nowrap',
            ...(positive ? { left: `calc(50% + ${fillPct}% + 6px)` } : { right: `calc(50% + ${fillPct}% + 6px)` }),
          }}
        >
          {formatGap(row.gap)}
        </Typography>
      </Box>
    </Box>
  )
}

interface AxisProps {
  ticks: number[]
  posFor: (value: number) => number
}

/** Full-height vertical gridlines behind the bars; the 0 line doubles as the genre baseline. */
const ChartGrid: FC<AxisProps> = ({ ticks, posFor }) => (
  <Box
    aria-hidden
    sx={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `${LABEL_COL}px 1fr`, gap: 1.25, px: 0.5, pointerEvents: 'none' }}
  >
    <Box />
    <Box sx={{ position: 'relative' }}>
      {ticks.map((tick) => (
        <Box
          key={tick}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${posFor(tick)}%`,
            transform: 'translateX(-50%)',
            width: tick === 0 ? '1.5px' : '1px',
            backgroundColor: (theme) => alpha(theme.palette.secondary.main, tick === 0 ? 0.22 : 0.08),
          }}
        />
      ))}
    </Box>
  </Box>
)

/** Tick-value labels rendered under the chart, aligned to the gridlines. */
const AxisLabels: FC<AxisProps> = ({ ticks, posFor }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_COL}px 1fr`, gap: 1.25, px: 0.5, mt: 0.75 }}>
    <Box />
    <Box sx={{ position: 'relative', height: 14 }}>
      {ticks.map((tick, index) => (
        <Typography
          key={tick}
          sx={{
            position: 'absolute',
            top: 0,
            left: `${posFor(tick)}%`,
            transform: index === 0 ? 'none' : index === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
            fontSize: '0.625rem',
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {formatTick(tick)}
        </Typography>
      ))}
    </Box>
  </Box>
)

interface ChartLegendProps {
  overLabel: string
  underLabel: string
}

/** Direction key shown under the diverging chart, aligned to the centre baseline. */
const ChartLegend: FC<ChartLegendProps> = ({ overLabel, underLabel }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_COL}px 1fr`, gap: 1.25, mt: 1 }}>
    <Box />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <LegendItem color="error.main" label={underLabel} align="flex-start" />
      <LegendItem color="success.main" label={overLabel} align="flex-end" />
    </Box>
  </Box>
)

const LegendItem: FC<{ color: string; label: string; align: 'flex-start' | 'flex-end' }> = ({ color, label, align }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: align }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: color }} />
    <Typography sx={{ fontSize: '0.625rem', fontWeight: 'bold', color: 'text.secondary', letterSpacing: '0.02em' }}>{label}</Typography>
  </Box>
)

interface IndexListProps {
  heading: string
  rows: PatternMixRow[]
  positive: boolean
}

/** A compact over/under-index list: pattern name + signed gap, led by a directional heading. */
const IndexList: FC<IndexListProps> = ({ heading, rows, positive }) => {
  const color = positive ? 'success.main' : 'error.main'
  const Icon = positive ? ArrowDropUpRounded : ArrowDropDownRounded
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.75 }}>
        <Icon sx={{ fontSize: '1.4rem', color, ml: '-4px', display: 'block' }} />
        <SummaryHeading>{heading}</SummaryHeading>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {rows.map((row) => (
          <Box key={row.id} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {row.label}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {formatGap(row.gap)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
